import {Component, Input, OnInit} from '@angular/core';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {TRAFFIC_LIGHT_STATUS_CLASS} from '@constants/machine/traffic-light/traffic-light-status';
import {TranslateService} from '@ngx-translate/core';
import {MenuItem} from '@models/sidebar/menu-item';
import {Machine} from '@models/machine/machine';
import {MachineService} from '@services/administration/machine/machine.service';
import {MenuItemIcon} from '@models/sidebar/menu-item-icon';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {Observable, of, ReplaySubject} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {RealtimePollingService} from '@services/realtime-polling/realtime-polling.service';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';
import {EnvironmentConfig} from '@environments/environment';
import {MachineDetailsService} from '@services/dashboard/machine-details.service';
import {SettingsService} from '@services/administration/settings/settings.service';

@Component({
    selector: 'scitis-page-header-logo-and-name',
    templateUrl: './page-header-logo-and-name.component.html',
    styleUrls: ['./page-header-logo-and-name.component.scss'],
    standalone: false
})
export class PageHeaderLogoAndNameComponent implements OnInit {

  @Input()
  public pageName: string = '';

  @Input()
  public pageType: MenuItemType = MenuItemType.NONE;

  @Input()
  public machineCategory: string | null = null;

  @Input()
  public machineId: string = '';

  @Input()
  public machineMenuItem: MenuItem | undefined;

  public machineName: string = '';
  public machineType: string = '';
  public machineIcon: string = '';
  public machine: Machine = new Machine();
  public machineCategoryIcon: string = '';
  public trafficLightClass: string = TRAFFIC_LIGHT_STATUS_CLASS.CANNOT_EVALUATE;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private MACHINE_DATA_URL_SUFFIX: string = '/api/web/tenant/:tenant/machine/:machine/tile?refresh=:refresh&sampling=:sampling';
  private SAMPLING_RATE: number = 1;
  private REFRESH_INTERVAL = EnvironmentConfig.PortalSettings.pagesRefreshInterval;

  constructor(
      private translate: TranslateService,
      private machineService: MachineService,
      private menuNotificationService: MenuNotificationService,
      private machineCategoryService: MachineCategoryService,
      private pollingService: RealtimePollingService,
      private settingsService: SettingsService,
      private machineDetailsService: MachineDetailsService,
  ) {
  }

  public get getTrafficTooltip(): string {
    switch (this.trafficLightClass) {
      case TRAFFIC_LIGHT_STATUS_CLASS.FAILURE:
      case TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE:
        return this.translate.instant('Machine malfunction');
      case TRAFFIC_LIGHT_STATUS_CLASS.WARNING:
        return this.translate.instant('Machine Warning or error-free switch off');
      case TRAFFIC_LIGHT_STATUS_CLASS.RUNNING:
        return this.translate.instant('Machine operating faultlessly');
      case TRAFFIC_LIGHT_STATUS_CLASS.INFO:
        return this.translate.instant('Machine info, debug, inhibit events');
      case TRAFFIC_LIGHT_STATUS_CLASS.CANNOT_EVALUATE:
        return this.translate.instant('Machine traffic light cannot be evaluated');
      default:
        return '';
    }
  }

  ngOnInit(): void {
    this.getImageLogo();
    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: result => {
        if (result) {
          this.initSubscription();
        }
      }
    });
  }

  public disableStatusMachine(): boolean {
    return this.pageType === MenuItemType.STATUS_EVENT_PAGE || this.pageType === MenuItemType.TEMPLATE_STATUS_EVENT_PAGE;
  }

  public getTooltip(): string {
    return this.translate.instant('Name') + ': ' + this.machineName + '\n'
        + this.translate.instant('Type') + ': ' + this.machineType + '\n'
        + this.translate.instant('Tag') + ': ' + this.machine.machineTag;
  }

  public onOpenStatusEventPage($event): void {
    this.preventOpeningDefaultItemFromLeftMenu($event);
    if (
        this.pageType !== MenuItemType.STATUS_EVENT_PAGE &&
        this.machine.trafficLightConfiguration &&
        this.machine.trafficLightConfiguration.statusEventPageId &&
        this.machine.trafficLightConfiguration.statusEventPageId > 0
    ) {
      const statusEventPageMenuItem: MenuItem = this.generateStatusEventPageMenuItem();
      this.menuNotificationService.broadcastClickOnDashboard(statusEventPageMenuItem);
    }
  }

  private generateStatusEventPageMenuItem(): MenuItem {
    const statusEventPageMenuItem: MenuItem = new MenuItem();
    statusEventPageMenuItem.pageId = String(this.machine.trafficLightConfiguration.statusEventPageId);
    statusEventPageMenuItem.icon = MenuItemIcon.STATUS_EVENT_PAGE;
    statusEventPageMenuItem.menuItemParent = this.machineMenuItem;
    statusEventPageMenuItem.id = this.machine.trafficLightConfiguration.statusEventPageMenuId;
    statusEventPageMenuItem.name = this.machine.trafficLightConfiguration.statusEventPageName;
    statusEventPageMenuItem.machineId = String(this.machine.id);
    statusEventPageMenuItem.organizationId = this.machine.parentId;
    statusEventPageMenuItem.type = MenuItemType.STATUS_EVENT_PAGE;
    statusEventPageMenuItem.machineCategory = this.machine.category;
    return statusEventPageMenuItem;
  }

  private getImageLogo(): void {
    if (this.machineCategory) {
      this.machineCategoryService.getMachineCategoryByName(this.machineCategory.toUpperCase()).subscribe({
        next: (result) => {
          this.machineCategoryIcon = result.icon;
        }, error: () => {
          console.log('Error getting Machine Category with ID: ' + this.machineCategory);
        }
      });
    }
  }

  private preventOpeningDefaultItemFromLeftMenu(event: Event): void {
    event.stopPropagation();
  }

  private startDataRetrievalForMachine(): Observable<any> {
    this.stopDataRetrievalForMachine(this.machineId);

    const pollingRequestParameters: RealtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: +this.machineId,
      organizationId: this.machine.parentId,
      refresh: this.REFRESH_INTERVAL,
      sampling: this.SAMPLING_RATE
    });
    return this.pollingService.start(this.MACHINE_DATA_URL_SUFFIX, pollingRequestParameters, String(this.machineId));
  }

  private stopDataRetrievalForMachine(machineId: string) {
    this.pollingService.stop(machineId);
  }

  private setTrafficLightClass(result): void {
    if (result) {
      const gatewaysStatus = this.machineDetailsService
      .determineAggregatesGatewaysStatus(result?.cloudplugs, result.missingConfiguration);
      this.trafficLightClass = this.machineDetailsService
      .determineTileTrafficLightClass(result, gatewaysStatus);
    } else {
      this.trafficLightClass = '';
    }
  }

  private initSubscription(): void {
    if (!this.disableStatusMachine()) {
      this.machineService.getMachineWithoutSignals(this.machineId)
      .pipe(takeUntil(this.destroy$), switchMap(machine => {
        if (machine) {
          this.machine = machine;
          return this.startDataRetrievalForMachine();
        }
        return of(null);
      }))
      .subscribe((res) => {
        this.setTrafficLightClass(res);
      });
    }
  }
}
